import clsx from "clsx";
import { useEffect, useRef, useState } from "react";

import SizeGuideModal from "@/components/atoms/SizeGuide/Modal/SizeGuideModal";
import SizeGuide from "@/components/atoms/SizeGuide/SizeGuide";
import { PDPProduct } from "../../page";
import type { Sizes } from "../ActionSection/ActionSection";
import styles from "./size-selector.module.css";

const SizeSelectorDropdown = ({
  sizes,
  comingSoon,
  product,
  showSizeGuide,
  position = "bottom",
  showStockStatus = true,
  onNotifyClick,
}: {
  sizes: Sizes;
  comingSoon: boolean;
  product: PDPProduct;
  showSizeGuide: boolean;
  position?: "top" | "bottom";
  showStockStatus?: boolean;
  onNotifyClick?: () => void;
}) => {
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };

    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open]);

  const selected = sizes.sizes?.find((s) => s.isSelected);
  const isRingProduct = product?.breadcrumbs?.some(
    (item) => item?.uri === "/products/jewelry/rings"
  );
  return (
    <div className={styles.dropdownWrapper} ref={dropdownRef}>
      <div className={styles.labelRow}>
        <label className={styles.sizeLabel}>Size</label>
        {showSizeGuide && (
          <SizeGuideModal isRingProduct={isRingProduct!}>
            <SizeGuide
              sizeguide={product?.sizeguide!}
              isRingProduct={isRingProduct!}
            />
          </SizeGuideModal>
        )}
      </div>

      <button
        className={styles.trigger}
        onClick={() => setOpen((prev) => !prev)}
        type="button"
      >
        {selected ? `${selected.name}` : "Select size"}
      </button>

      {open && (
        <ul
          className={clsx(styles.dropdownList, {
            [styles.listTop]: position === "top",
            [styles.listBottom]: position === "bottom",
          })}
        >
          {sizes.sizes?.map((size) => (
            <li key={size.itemId} className={styles.option}>
              <div
                role="button"
                tabIndex={0}
                className={clsx(styles.optionButton, {
                  [styles.disabled]: showStockStatus && !size.stock,
                })}
                onClick={() => {
                  if (!showStockStatus || size.stock) {
                    size.selectSize();
                    setOpen(false);
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    if (!showStockStatus || size.stock) {
                      size.selectSize();
                      setOpen(false);
                    }
                  }
                }}
              >
                <span className={styles.sizeName}>{size.name}</span>

                {showStockStatus && (
                  <span>
                    {(!size.stock || comingSoon) && (
                      <span className={styles.notify}>
                        {comingSoon ? "Coming soon." : "Out of stock."}{" "}
                        <button
                          className={styles.notifyLink}
                          onClick={(e) => {
                            e.stopPropagation();
                            size.selectSize();
                            onNotifyClick?.();
                            setOpen(false);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              if (!showStockStatus || size.stock) {
                                size.selectSize();
                                setOpen(false);
                              }
                            }
                          }}
                        >
                          Notify me
                        </button>
                      </span>
                    )}

                    {size.fewLeft && size.stock && (
                      <span className={styles.lowStock}>Only a few left</span>
                    )}
                  </span>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default SizeSelectorDropdown;
